import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/shared/ui';
import {
	buildKtasIframeUrl,
	getKtasAppOrigin,
	getKtasErrorMessage,
	getKtasIframeScale,
	requestKtasPlayToken,
} from '@/services/ktasPlayService';

interface KtasPlayModalProps {
	classId: string | number;
	/** 실습 모드 값 (1: 학습, 2: 연습, 3: 평가) */
	mode: string;
	title: string;
	onClose: () => void;
}

type Phase = 'loading' | 'confirm' | 'playing' | 'error';

/**
 * KTAS iframe 전체 화면 플레이어
 * - 열릴 때 play token 발급 → 끝나지 않은 플레이가 있으면 이어하기/새로하기 선택
 * - iframe 앱이 보내는 `closeIframe` postMessage 를 받으면 닫는다
 */
export default function KtasPlayModal({ classId, mode, title, onClose }: KtasPlayModalProps): React.ReactElement {
	const { t } = useTranslation('class');
	const [phase, setPhase] = useState<Phase>('loading');
	const [iframeUrl, setIframeUrl] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const iframeScale = getKtasIframeScale();
	/** 이어하기 선택 시 재사용할 (이미 발급된) play token */
	const pendingTokenRef = useRef('');
	/** StrictMode 에서 effect 가 두 번 실행돼도 /new 는 한 번만 호출 */
	const requestedRef = useRef(false);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const fail = useCallback(
		(error: unknown) => {
			if (!mountedRef.current) return;
			setErrorMessage(getKtasErrorMessage(error, t('detail.ktasError', { defaultValue: 'KTAS를 시작하지 못했습니다.' })));
			setPhase('error');
		},
		[t]
	);

	const issueToken = useCallback(
		async (renew: boolean) => {
			setPhase('loading');
			try {
				const res = await requestKtasPlayToken({ classId, mode, renew });
				if (!mountedRef.current) return;
				if (res.unfinished && !renew) {
					pendingTokenRef.current = res.play_token;
					setPhase('confirm');
					return;
				}
				setIframeUrl(buildKtasIframeUrl(res.play_token));
				setPhase('playing');
			} catch (e) {
				fail(e);
			}
		},
		[classId, mode, fail]
	);

	useEffect(() => {
		if (requestedRef.current) return;
		requestedRef.current = true;
		void issueToken(false);
	}, [issueToken]);

	/** iframe 앱 종료 이벤트 */
	useEffect(() => {
		const appOrigin = getKtasAppOrigin();
		const handleMessage = (event: MessageEvent) => {
			if (appOrigin && event.origin !== appOrigin) return;
			const data = event.data as { eventType?: string } | null;
			if (data && data.eventType === 'closeIframe') {
				onClose();
			}
		};
		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [onClose]);

	const handleContinue = () => {
		setIframeUrl(buildKtasIframeUrl(pendingTokenRef.current));
		setPhase('playing');
	};

	const handleStartNew = () => {
		pendingTokenRef.current = '';
		void issueToken(true);
	};

	return createPortal(
		<div className="fixed inset-0 z-[1000] flex flex-col bg-white" role="dialog" aria-modal="true" aria-label={title}>
			{/* 플레이 중에는 헤더 없이 구 프론트(`section.play-wrapper`)처럼 iframe 이 뷰포트 전체(100vw×100vh)를 쓴다.
			    iframe 앱은 자체 높이(h-dvh 등)로 레이아웃하므로 바깥에서 높이를 깎거나 따로 스크롤시키지 않는다.
			    종료는 iframe 앱의 닫기 버튼(closeIframe 메시지)으로 처리한다. */}
			{phase !== 'playing' && (
				<div className="flex h-12 shrink-0 items-center justify-between border-b border-gray-200 px-4">
					<h2 className="truncate text-sm font-semibold text-gray-900">{title}</h2>
					<button
						type="button"
						onClick={onClose}
						className="rounded p-1 text-gray-500 hover:bg-gray-100"
						aria-label={t('detail.ktasClose', { defaultValue: '닫기' })}
					>
						<X size={20} />
					</button>
				</div>
			)}

			<div className="relative min-h-0 flex-1">
				{phase === 'loading' && (
					<div className="flex h-full items-center justify-center gap-2 text-gray-500">
						<Loader2 className="animate-spin" size={20} />
						<span>{t('detail.ktasLoading', { defaultValue: 'KTAS를 준비하고 있습니다...' })}</span>
					</div>
				)}

				{phase === 'confirm' && (
					<div className="flex h-full flex-col items-center justify-center gap-6 px-6 text-center">
						<p className="text-base text-gray-800">
							{t('detail.ktasContinueTitle', {
								defaultValue: '진행 중인 실습이 있습니다. 이어서 진행하시겠습니까?',
							})}
						</p>
						<div className="flex gap-3">
							<Button type="button" onClick={handleContinue}>
								{t('detail.ktasContinue', { defaultValue: '이어하기' })}
							</Button>
							<Button type="button" variant="secondary" onClick={handleStartNew}>
								{t('detail.ktasStartNew', { defaultValue: '새로 시작' })}
							</Button>
						</div>
					</div>
				)}

				{phase === 'error' && (
					<div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
						<p className="text-base text-gray-800">{errorMessage}</p>
						<div className="flex gap-3">
							<Button type="button" onClick={() => void issueToken(false)}>
								{t('detail.ktasRetry', { defaultValue: '다시 시도' })}
							</Button>
							<Button type="button" variant="secondary" onClick={onClose}>
								{t('detail.ktasClose', { defaultValue: '닫기' })}
							</Button>
						</div>
					</div>
				)}

				{phase === 'playing' && iframeUrl && (
					/* iframe 을 1/scale 배로 키운 뒤 scale 로 줄여, 앱이 더 큰 화면으로 인식하게 한다(잘려서 안 보이던 아래쪽까지 표시) */
					<div className="relative h-full w-full overflow-hidden">
						<iframe
							title={title}
							src={iframeUrl}
							className="absolute left-0 top-0 block border-0"
							style={{
								width: `${100 / iframeScale}%`,
								height: `${100 / iframeScale}%`,
								transform: `scale(${iframeScale})`,
								transformOrigin: '0 0',
							}}
							allow="autoplay; fullscreen"
							allowFullScreen
						/>
					</div>
				)}
			</div>
		</div>,
		document.body
	);
}
